// client/src/App.tsx (FULL UPDATED FILE)
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ToastProvider } from './context/ToastContext';
import LoadingState from './components/LoadingState';
// Kept eager: the layout shell wraps every route and PermissionRoute gates them,
// so both are needed on the very first render — lazy-loading them would just add
// a redundant loading flash.
import { MainLayout } from './layouts/MainLayout';
import { PermissionRoute } from './components/PermissionRoute';
// Tiny and needed the moment connectivity drops, including on /login — not lazy.
import OfflineBanner from './components/OfflineBanner';
import { usePageTracking } from './hooks/usePageTracking';

import './App.css';

// ── Route-level code splitting ─────────────────────────────────────────────
// Every page used to be imported eagerly, so the whole app — including the
// 2,000–3,600-line pages — shipped in ONE bundle. The browser had to download
// and parse all of it before the first screen, and that weight made switching
// modules sluggish. Loading each page as its own lazy chunk shrinks the initial
// bundle sharply and makes navigation fast: the Suspense boundary in AppRoutes
// shows LoadingState while a page's chunk loads (once, then it's cached).
const lazyNamed = (loader: () => Promise<any>, name: string) =>
  lazy(() => loader().then((m) => ({ default: m[name] })));

// framer-motion is only used by the redesigned login — keep it out of app chunks.
const Login = lazy(() => import('./components/Login'));

const ForgotPasswordPage = lazyNamed(() => import('./pages/ForgotPasswordPage'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyNamed(() => import('./pages/ResetPasswordPage'), 'ResetPasswordPage');
const AppDashboard = lazy(() => import('./components/AppDashboard'));
const RfqInboxPage = lazyNamed(() => import('./features/rfqInbox/RfqInboxPage'), 'RfqInboxPage');
const UsersPage = lazyNamed(() => import('./pages/UsersPage'), 'UsersPage');
const SettingsPage = lazyNamed(() => import('./pages/SettingsPage'), 'SettingsPage');
const ReportsPage = lazyNamed(() => import('./pages/ReportsPage'), 'ReportsPage');
const QuotationsPage = lazyNamed(() => import('./pages/QuotationsPage'), 'QuotationsPage');
const QuotationDetailPage = lazyNamed(() => import('./pages/QuotationDetailPage'), 'QuotationDetailPage');
const ClientsPage = lazyNamed(() => import('./pages/ClientsPage'), 'ClientsPage');
const ConfirmedOrdersPage = lazyNamed(() => import('./pages/ConfirmedOrdersPage'), 'ConfirmedOrdersPage');
const ConfirmedOrderDetailPage = lazyNamed(() => import('./pages/ConfirmedOrderDetailPage'), 'ConfirmedOrderDetailPage');
const ClientInvoicePage = lazyNamed(() => import('./pages/ClientInvoicePage'), 'ClientInvoicePage');
const ClientInvoicesPage = lazyNamed(() => import('./pages/ClientInvoicesPage'), 'ClientInvoicesPage');
const CreditNotesPage = lazyNamed(() => import('./pages/CreditNotesPage'), 'CreditNotesPage');
const CreditNoteDetailPage = lazyNamed(() => import('./pages/CreditNoteDetailPage'), 'CreditNoteDetailPage');
const VendorPurchaseOrdersPage = lazyNamed(() => import('./pages/VendorPurchaseOrdersPage'), 'VendorPurchaseOrdersPage');
const VendorPurchaseOrderDetailPage = lazyNamed(() => import('./pages/VendorPurchaseOrderDetailPage'), 'VendorPurchaseOrderDetailPage');
const VendorsPage = lazyNamed(() => import('./pages/VendorsPage'), 'VendorsPage');
const PriceListPage = lazyNamed(() => import('./pages/PriceListPage'), 'PriceListPage');
const DataSheetsPage = lazyNamed(() => import('./pages/DataSheetsPage'), 'DataSheetsPage');
const ClientPriceListsPage = lazy(() => import('./pages/ClientPriceListsPage'));
const ClientPriceListDetailPage = lazy(() => import('./pages/ClientPriceListDetailPage'));
const ClientPriceListResponsePage = lazy(() => import('./pages/ClientPriceListResponsePage'));
const TemplateManagementPage = lazy(() => import('./pages/TemplateManagementPage'));
const CurrencyConversionPage = lazyNamed(() => import('./pages/CurrencyConversionPage'), 'CurrencyConversionPage');
const VendorInvoicesPage = lazyNamed(() => import('./pages/VendorInvoicesPage'), 'VendorInvoicesPage');
const AccountsPage = lazyNamed(() => import('./pages/AccountsPage'), 'AccountsPage');
const AccountsPayableDetailPage = lazyNamed(() => import('./pages/AccountsPayableDetailPage'), 'AccountsPayableDetailPage');
const AccountsPayablePaymentVoucherPage = lazyNamed(() => import('./pages/AccountsPayablePaymentVoucherPage'), 'AccountsPayablePaymentVoucherPage');
const AccountsPayableVouchersPage = lazyNamed(() => import('./pages/AccountsPayableVouchersPage'), 'AccountsPayableVouchersPage');
const AccountsSupplierDetailPage = lazyNamed(() => import('./pages/AccountsSupplierDetailPage'), 'AccountsSupplierDetailPage');
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const MasterCataloguePage = lazy(() => import('./pages/MasterCataloguePage'));

// Scope: only these five areas are reachable in the UI right now. Everything else is hidden
// from the sidebar (Sidebar.tsx ENABLED_KEYS) AND blocked here, because hiding a nav link does
// not lock anything — every route is still reachable by typing the URL, from a bookmark, or
// from an in-app link on a page that is still visible.
//
// Enforced in ProtectedRoute rather than on ~40 individual <Route> elements: every authenticated
// route already passes through here, so one list cannot drift out of sync with the router, and
// re-enabling a module is a one-line edit.
//
// FRONTEND ONLY. No API route, permission, service or job is touched — the backend still serves
// every module, so data written by earlier work (confirmed orders, invoices, POs) keeps flowing
// and nothing already in the database becomes unreachable to the server.
const ENABLED_PATH_PREFIXES = [
  '/rfq',              // RFQ Inbox
  '/quotations',       // Quote Generator (+ /quotations/:id)
  '/clients',          // Client List
  '/vendors',          // Vendor List
  '/master-catalogue', // Master Catalogue
];

// Segment-aware so '/vendors' cannot accidentally admit '/vendor-invoices'.
const isEnabledPath = (pathname: string) =>
  ENABLED_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div className="min-h-screen"><LoadingState /></div>;
  }

  // ✅ Important: replace prevents back-button returning into app after logout
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Out-of-scope module (including /app, the old landing dashboard) — send them to the Inbox.
  if (!isEnabledPath(location.pathname)) return <Navigate to="/rfq" replace />;

  return <>{children}</>;
};

// Public Route Component (redirect if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="min-h-screen"><LoadingState /></div>;
  }

  // ✅ Important: replace avoids history weirdness
  // Demo scope: land on Inbox, not the Home dashboard (Home is hidden from nav for now)
  return !isAuthenticated ? <>{children}</> : <Navigate to="/rfq" replace />;
};

function AppRoutes() {
  usePageTracking(); // GA4 page_view + Clarity screen tag on every route change
  return (
    <Suspense fallback={<div className="min-h-screen"><LoadingState /></div>}>
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Section A — Public client-facing price list response form (no auth) */}
      <Route path="/respond/:id" element={<ClientPriceListResponsePage />} />

      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/rfq"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'quotations', type: 'view' }}>
              <MainLayout>
                <RfqInboxPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quotations"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'quotations', type: 'view' }}>
              <MainLayout>
                <QuotationsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quotations/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'quotations', type: 'view' }}>
              <MainLayout>
                <QuotationDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/confirmed-orders"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'confirmed_orders', type: 'view' }}>
              <MainLayout>
                <ConfirmedOrdersPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/confirmed-orders/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'confirmed_orders', type: 'view' }}>
              <MainLayout>
                <ConfirmedOrderDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/client-invoices"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <ClientInvoicesPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/client-invoices/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <ClientInvoicePage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/confirmed-orders/:id/invoice"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <ClientInvoicePage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Credit Notes */}
      <Route
        path="/credit-notes"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <CreditNotesPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/credit-notes/new"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <CreditNoteDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/credit-notes/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <CreditNoteDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/vendor-purchase-orders"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'vendor_purchase_orders', type: 'view' }}>
              <MainLayout>
                <VendorPurchaseOrdersPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/vendor-purchase-orders/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'vendor_purchase_orders', type: 'view' }}>
              <MainLayout>
                <VendorPurchaseOrderDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Vendors */}
      <Route
        path="/vendors"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'vendors', type: 'view' }}>
              <MainLayout>
                <VendorsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Price List */}
      <Route
        path="/price-list"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'products', type: 'view' }}>
              <MainLayout>
                <PriceListPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Product Data Sheets (PDS/TDS sent with chemicals quotations) */}
      <Route
        path="/data-sheets"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'products', type: 'view' }}>
              <MainLayout>
                <DataSheetsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Client Price List (from Master Catalog) */}
      <Route
        path="/client-price-lists"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'products', type: 'view' }}>
              <MainLayout>
                <ClientPriceListsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-price-lists/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'products', type: 'view' }}>
              <MainLayout>
                <ClientPriceListDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Inventory (NEW) */}
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'inventory', type: 'view' }}>
              <MainLayout>
                <InventoryPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Vendor Invoices */}
      <Route
        path="/vendor-invoices"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <VendorInvoicesPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsPayableDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts/payments/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsPayablePaymentVoucherPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts/vouchers"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsPayableVouchersPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts/suppliers/:supplierName"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsSupplierDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Master Catalogue */}
      <Route
        path="/master-catalogue"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'products', type: 'view' }}>
              <MainLayout>
                <MasterCataloguePage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'users', type: 'view' }}>
              <MainLayout>
                <UsersPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'clients', type: 'view' }}>
              <MainLayout>
                <ClientsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'templates', type: 'view' }}>
              <MainLayout>
                <TemplateManagementPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* Legacy roles route - redirect to users page */}
      <Route path="/roles" element={<Navigate to="/users" replace />} />

      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'reports', type: 'view' }}>
              <MainLayout>
                <ReportsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'settings', type: 'view' }}>
              <MainLayout>
                <SettingsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Currency Conversion */}
      <Route
        path="/currency-conversion"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'settings', type: 'view' }}>
              <MainLayout>
                <CurrencyConversionPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* Legacy admin route - redirect to users page */}
      <Route path="/admin" element={<Navigate to="/users" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage] = useState(
    'Your session has expired. Please log in again to continue.'
  );

  useEffect(() => {
    const handler = (_e: Event) => {
      setSessionExpiredOpen(true);
    };
    window.addEventListener('session-expired', handler as EventListener);
    return () => window.removeEventListener('session-expired', handler as EventListener);
  }, []);

  const handleLoginAgain = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('auth');
      localStorage.removeItem('authToken');
    } catch (_) {}
    window.location.href = '/login';
  };

  return (
    <AuthProvider>
      <NotificationProvider>
      <ToastProvider>
      <Router>
        <OfflineBanner />
        <div className="App">
          <AppRoutes />
        </div>
        {sessionExpiredOpen && (
          <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/30 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
              <div className="text-sm font-semibold text-slate-800">Session expired</div>
              <div className="mt-2 text-xs text-slate-600">
                {sessionExpiredMessage}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={handleLoginAgain}
                  className="px-4 py-2 rounded-md bg-[#0A6AAC] text-white text-xs font-semibold hover:bg-[#085489]"
                >
                  Login again
                </button>
              </div>
            </div>
          </div>
        )}
      </Router>
      </ToastProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
